import { getPersistedFormData } from '@eti/utils';
import type { ContactDetails } from '@eti/schema-types';
import { TRAVELER_DETAILS } from '../../../constants/formNamesConstants';
import type { Country, FormData } from '../models';
import { CONTACT_INFO_FORM_SECTION, contactFields, contactTypes } from '../constants/formFields';
import type { CountryType } from '../../../common/utils/models';

const {
  ARRANGER_FIRST_NAME,
  ARRANGER_LAST_NAME,
  CONTACT_TYPE,
  EMAIL,
  EMAIL_CONFIRM,
  EMERGENCY_LAST_NAME,
  EMERGENCY_PHONE_COUNTRY_ID,
  EMERGENCY_PHONE,
  NEWSLETTER,
  PHONE,
  PHONE_COUNTRY_ID,
} = contactFields;

export const getFieldValue = (persistedValue: string, fallback?: string | number | null) => {
  return persistedValue && persistedValue.toString().length > 0 ? persistedValue : fallback;
};

export const getCountryIdFromPrefix = (
  countries: Country[],
  prefix?: string | null,
): number | null => {
  if (!prefix) {
    return null;
  }

  const country = countries.find((c) => c.phoneCountryPrefix === prefix);

  return country?.id ?? null;
};

export const getInitialValuesForContactInformationForm = (
  country: CountryType,
  countries: Country[],
  shouldUsePersistedFormData: boolean = true,
  isTravelArrangerEnabled?: boolean,
  showConfirmEmail?: boolean,
  showEmergencyContactFields?: boolean,
  mainTravelerContactInformation?: ContactDetails | null,
) => {
  const persistedFormData = shouldUsePersistedFormData
    ? getPersistedFormData(TRAVELER_DETAILS)
    : {};
  const mainTravelerEmail = mainTravelerContactInformation?.email;
  const mainTravelerPhone = mainTravelerContactInformation?.phone?.numberWithoutCountryCode;
  const mainTravelerCountryId = getCountryIdFromPrefix(
    countries,
    mainTravelerContactInformation?.phone?.countryCallingCode,
  );

  return {
    [CONTACT_INFO_FORM_SECTION]: {
      ...(isTravelArrangerEnabled && {
        [CONTACT_TYPE]: persistedFormData[CONTACT_TYPE] ?? contactTypes.TRAVELER,
        [ARRANGER_FIRST_NAME]: persistedFormData[ARRANGER_FIRST_NAME],
        [ARRANGER_LAST_NAME]: persistedFormData[ARRANGER_LAST_NAME],
      }),
      [EMAIL]: getFieldValue(persistedFormData[EMAIL], mainTravelerEmail),
      ...(showConfirmEmail && {
        [EMAIL_CONFIRM]: persistedFormData[EMAIL_CONFIRM],
      }),
      [PHONE_COUNTRY_ID]:
        getFieldValue(persistedFormData[PHONE_COUNTRY_ID], mainTravelerCountryId) ?? country?.id,
      [PHONE]: getFieldValue(persistedFormData[PHONE], mainTravelerPhone),
      [NEWSLETTER]: persistedFormData[NEWSLETTER],
      ...(showEmergencyContactFields && {
        [EMERGENCY_LAST_NAME]: persistedFormData[EMERGENCY_LAST_NAME],
        [EMERGENCY_PHONE_COUNTRY_ID]: persistedFormData[EMERGENCY_PHONE_COUNTRY_ID] ?? country?.id,
        [EMERGENCY_PHONE]: persistedFormData[EMERGENCY_PHONE],
      }),
    },
  };
};

export const transformContactInformationFormData = (formValues: FormData, countries: Country[]) => {
  const contactInfo = formValues[CONTACT_INFO_FORM_SECTION];
  const countryId = contactInfo[PHONE_COUNTRY_ID];
  const country = countries.find((c) => c.id === countryId);
  const emergencyContactCountryId = contactInfo[EMERGENCY_PHONE_COUNTRY_ID];
  const emergencyContactCountry = countries.find((c) => c.id === emergencyContactCountryId);
  const subscribeToNewsletter = !contactInfo[NEWSLETTER];

  const contactInformation = {
    email: contactInfo[EMAIL] ?? '',
    cellPhone: {
      countryId: country ? String(country.id) : '',
      phoneNumberAfterPrefix: contactInfo[PHONE] ?? '',
      prefixId: country?.phoneCountryId ?? '',
    },
  };

  const emergencyContactInformation = {
    lastName: contactInfo[EMERGENCY_LAST_NAME] ?? '',
    phoneNumber: {
      countryId: emergencyContactCountry ? String(emergencyContactCountry.id) : '',
      phoneNumberAfterPrefix: contactInfo[EMERGENCY_PHONE] ?? '',
      prefixId: emergencyContactCountry?.phoneCountryId ?? '',
    },
  };

  return { contactInformation, subscribeToNewsletter, emergencyContactInformation };
};
